import { useEffect, useRef, useState } from "react";

import {
  DEFAULT_FLAGS,
  loadLastGoodFlags,
  parseGuardWebFlags,
  persistLastGoodFlags,
  type GuardWebFlags,
} from "./feature-flags";

// Server-verdict flag resolution (hardened per Codex review, 2026-06-10 — the hosted resolver
// `get_guard_client_flags` is live, so an unverified verdict now FAILS CLOSED):
//   - source "cached"  → a verdict this exact account+user received before (offline cold starts).
//   - source "server"  → the live verdict; persisted scoped for next time.
//   - source "default" → NO scoped verdict exists. The shell must NOT render capture from this:
//     while `loading` it shows a checking state; if the resolver errors and nothing scoped is
//     cached, it fails closed. DEFAULT_FLAGS only seeds parse-merging, never grants access.

/** Structural slice of supabase-js we call — keeps tests free of the generated Json typing. */
export interface FlagsRpcClient {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<{ data: unknown; error: { message: string } | null }>;
}

export interface FeatureFlagsState {
  flags: GuardWebFlags;
  /** Where the current verdict came from. "default" = NO verdict — callers fail closed on it. */
  source: "default" | "cached" | "server";
  loading: boolean;
}

interface ScopedFeatureFlagsState extends FeatureFlagsState {
  scopeKey: string;
}

interface StringStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): StringStorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function useFeatureFlags(
  client: FlagsRpcClient,
  accountId: string,
  userId: string,
  options?: { storage?: StringStorageLike },
): FeatureFlagsState {
  const storageRef = useRef<StringStorageLike | null>(options?.storage ?? defaultStorage());
  const scopeKey = JSON.stringify([accountId, userId]);
  const [state, setState] = useState<ScopedFeatureFlagsState>(() => ({
    scopeKey,
    ...initialState(storageRef.current, accountId, userId),
  }));

  // A different account/user signing in on the same browser must re-resolve from ITS own scope.
  // Never return the previous user's verdict for even one paint; render from the new scoped cache
  // immediately, then let the effect settle the canonical state.
  const visibleState =
    state.scopeKey === scopeKey ? state : { scopeKey, ...initialState(storageRef.current, accountId, userId) };

  useEffect(() => {
    let cancelled = false;
    setState((previous) =>
      previous.scopeKey === scopeKey ? previous : { scopeKey, ...initialState(storageRef.current, accountId, userId) },
    );
    void (async () => {
      try {
        const result = await client.rpc("get_guard_client_flags", { active_account_id: accountId });
        if (cancelled) {
          return;
        }
        if (result.error !== null) {
          // Resolver missing/denied — keep the scoped cached verdict if we had one; otherwise the
          // state stays source:"default" and the shell fails closed.
          setState((previous) =>
            previous.scopeKey === scopeKey ? { ...previous, loading: false } : previous,
          );
          return;
        }
        if (storageRef.current !== null) {
          persistLastGoodFlags(storageRef.current, { accountId, userId }, result.data);
        }
        setState({ scopeKey, flags: parseGuardWebFlags(result.data), source: "server", loading: false });
      } catch {
        if (!cancelled) {
          setState((previous) =>
            previous.scopeKey === scopeKey ? { ...previous, loading: false } : previous,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, accountId, userId, scopeKey]);

  return {
    flags: visibleState.flags,
    source: visibleState.source,
    loading: visibleState.loading,
  };
}

function initialState(storage: StringStorageLike | null, accountId: string, userId: string): FeatureFlagsState {
  const cached = storage === null ? null : loadLastGoodFlags(storage, { accountId, userId });
  return cached === null
    ? { flags: DEFAULT_FLAGS, source: "default", loading: true }
    : { flags: cached, source: "cached", loading: true };
}
